/**
 * How far the work has got - and, since this card grew a second job, WHO gets to
 * say so.
 *
 * It moved out of `ProjectOverview` when it stopped being markup. There are now
 * three separate things in here that a reader has to hold at once: a local copy
 * of the slider value, a rule for which of two percentages is the true one, and
 * a preview of what changing that rule would cost. The rest of the overview is
 * label-and-figure rows, and burying a state machine among them is how a card
 * ends up with a bug nobody can see (it had one - see below).
 *
 * ## The bug this card exists to end
 *
 * `progressPct` on the project row is a number an operator typed. It was also
 * the only number the bar ever drew, so a project whose every task was finished
 * could sit at the 20% somebody set in March and look stalled. `progressSource`
 * is the fix: under "tasks" the stored number is dead weight and the real figure
 * is the task rollup, which the summary endpoint recomputes on every read.
 *
 * ## Two percentages, and why both travel
 *
 * `summary.progressPct` is the EFFECTIVE number - the server has already applied
 * the source, so it is what a screen prints. `summary.derivedProgressPct` is
 * what the tasks say REGARDLESS of the setting, and it is here for exactly one
 * purpose: the operator has to be able to read what switching would do BEFORE
 * switching. Without it, "let the tasks decide" is a button whose consequence
 * you learn by pressing it, on a figure the client sees.
 *
 * Switching never sends a percentage. The server keeps `project.progressPct`
 * untouched while the source is "tasks", so going back to "manual" restores the
 * number the operator typed - and a card that helpfully "synced" the two would
 * destroy it on the way out.
 */
import { useEffect, useState } from "react";
import { useT } from "../../../i18n/I18nProvider";
import type { Project, ProjectSummary } from "../../../lib/projects";
import { Card, PillTabs, fmtPct, type PillTab } from "../../ui";

type Source = Project["progressSource"];

/**
 * The percentage this card prints, out of the three numbers in scope.
 *
 * A function rather than a ternary because the interesting case is the one a
 * ternary hides: the project row and the summary are two fetches, and a source
 * change lands on the project first. For the frame between the PATCH and the
 * summary refetch, `summary.progressPct` was computed under the OLD rule - the
 * toggle would have already moved while the bar still showed the number the
 * operator just voted out. So the summary's own `progressSource` is checked
 * against the project's, and while they disagree the card falls back to the
 * ingredient that is true under either: the task rollup travels regardless of
 * the setting, and the stored figure is whatever the slider last wrote.
 *
 * `manual` is the LOCAL slider value, not `project.progressPct`, so the figure
 * follows the thumb while a drag is in flight.
 */
function shownPct(summary: ProjectSummary | null, source: Source, manual: number): number {
  if (!summary) return manual;
  if (summary.progressSource === source) return summary.progressPct;
  return source === "tasks" ? summary.derivedProgressPct : manual;
}

export function ProgressCard({
  project,
  summary,
  onProgress,
  onProgressSource,
}: {
  project: Project;
  summary: ProjectSummary | null;
  onProgress: (v: number) => void;
  onProgressSource: (s: Source) => void;
}) {
  const t = useT();

  // The slider is controlled off a local copy so that the percentage above it
  // follows the thumb while dragging. The effect is the other half of that: the
  // roadmap sets progressPct by dropping a card, and an uncontrolled input
  // would keep showing whatever it was left at.
  //
  // Seeded from the STORED number and never from the effective one. Under
  // "tasks" the effective figure is the task rollup, and a slider holding that
  // would hand the operator the derived percentage the moment they switched
  // back to manual - the first nudge would then write it over the number they
  // had typed, which is the one thing switching back is supposed to recover.
  const [progress, setProgress] = useState(project.progressPct);
  useEffect(() => {
    setProgress(project.progressPct);
  }, [project.progressPct]);

  // The source is read off the PROJECT, not the summary: it is the field the
  // toggle writes, so the pill moves as soon as the PATCH lands rather than
  // waiting for a second fetch. `shownPct` is what reconciles the two.
  const source = project.progressSource;
  // With no summary there is no rollup to switch to and no preview to show, so
  // the card is the plain manual slider it was before this setting existed -
  // note the `summary != null`, which is why "tasks" alone is not enough. The
  // toggle must not appear before the data it writes against: firing it there
  // would trade a number a person set for a number nobody can see.
  const fromTasks = summary != null && source === "tasks";
  const shown = shownPct(summary, source, progress);

  const tabs: PillTab<Source>[] = [
    { value: "manual", label: t("projects.progressManual") },
    { value: "tasks", label: t("projects.progressFromTasks") },
  ];

  return (
    <Card className="p-5">
      <div className="flex items-baseline gap-3 text-[12.75px]">
        <span className="crm-mut">{t("projects.progress")}</span>
        <span className="crm-push crm-tnum text-[15px] font-bold">{fmtPct(shown, 0)}</span>
      </div>

      {fromTasks ? (
        // The same bar the budget card next to it draws, rather than the
        // `Progress` component: that one stacks its own figure on top of the
        // track for a list column, and this card already has a heading row. Two
        // cards side by side, one draggable and one not, should differ in that
        // and in nothing else.
        <span
          className="crm-fbar"
          role="progressbar"
          aria-label={t("projects.progress")}
          aria-valuenow={Math.round(shown)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i style={{ width: `${Math.min(100, Math.max(0, shown))}%` }} />
        </span>
      ) : (
        /* Committed on release rather than on every step: dragging from 0 to
           80 otherwise sends sixteen writes, and the last one to land wins.
           The commit reads the input rather than the state for the same
           reason - the value under the thumb is the one the operator let go
           of, whatever React has got round to re-rendering. */
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={progress}
          onChange={(e) => setProgress(Number(e.target.value))}
          aria-label={t("projects.progress")}
          onMouseUp={(e) => onProgress(Number((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => onProgress(Number((e.target as HTMLInputElement).value))}
          onKeyUp={(e) => onProgress(Number((e.target as HTMLInputElement).value))}
          className="mt-3 w-full accent-[var(--color-primary)]"
        />
      )}

      {summary ? (
        <div className="mt-4 border-t border-[var(--crm-hair)] pt-3.5">
          <span className="crm-mut block text-[11px] font-bold uppercase tracking-wide">
            {t("projects.progressSource")}
          </span>
          <PillTabs small tabs={tabs} value={source} onChange={onProgressSource} className="mt-2" />
          {fromTasks ? (
            // Why the slider is gone, said where the slider used to be. An
            // operator who finds a control missing assumes the page is broken
            // long before they assume it is a setting.
            <p className="crm-mut mt-2.5 text-[12px] leading-relaxed">{t("projects.progressFromTasksHint")}</p>
          ) : (
            // The price of the pill next to it, before it is pressed. Built as
            // two nodes rather than one interpolated sentence so the figure
            // keeps the tabular numerals every other percentage on this tab is
            // set in, and so neither half needs a catalogue entry of its own.
            <p className="mt-2.5 flex items-baseline gap-2 text-[12px]">
              <span className="crm-mut">{t("projects.progressFromTasks")}</span>
              <span className="crm-tnum font-bold text-foreground">
                {fmtPct(summary.derivedProgressPct, 0)}
              </span>
            </p>
          )}
        </div>
      ) : null}
    </Card>
  );
}
